import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { KnowledgeRecentEntityItem } from 'src/app/models/home/knowledge-attention/knowledge-attention.model';
import {
  EntityGenerationSource,
  EntitySuggestedQuestion,
  HomeEntityChatNavigationState,
} from 'src/app/models/home/entity-capabilities/home-entity-capabilities.model';
import { SelectableSource, GenerateNavigationState } from 'src/app/models/generation/generation.model';
import { HomeEntityQuestionsService } from 'src/app/services/home-ia/home-entity-questions.service';
import { HomeEntityGenerationService } from 'src/app/services/home-ia/home-entity-generation.service';
import { PlanFeatureService } from 'src/app/features/plans/services/plan-feature.service';
import { DOC_GENERATION_FEATURE_CODE } from 'src/app/features/plans/models/entitlement.model';
import { LanguageService } from 'src/app/services/language/language.service';

/**
 * Home IA - Capacidades por entidad (H10/H11/H12) disparadas desde una entidad visible del radar R4.
 * READ-ONLY salvo la accion explicita del usuario. Aisla sus propios estados loading/empty/error para
 * que un fallo NUNCA tumbe /home: los datos del radar siguen visibles aunque estas acciones fallen.
 *
 * - H10: al expandir carga preguntas sugeridas (chips). Clic en un chip NO ejecuta la query; navega
 *   al chat con el texto precargado (H11) y el usuario confirma y envia.
 * - H12: "Generar documento" abre un preview de FUENTES visibles con confirmacion explicita; solo
 *   tras confirmar navega al wizard de generacion R2 pasando las fuentes. Gated doc_generation.
 */
@Component({
  selector: 'app-home-entity-actions',
  templateUrl: './home-entity-actions.component.html',
  styleUrls: ['./home-entity-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HomeEntityActionsComponent implements OnInit, OnDestroy {
  @Input({ required: true }) entity!: KnowledgeRecentEntityItem;

  private readonly destroy$ = new Subject<void>();

  expanded = false;

  // H10 - preguntas sugeridas
  questions: EntitySuggestedQuestion[] = [];
  questionsLoading = false;
  questionsError = false;
  private questionsLoaded = false;

  // H12 - generacion desde entidad
  docGenerationEnabled = false;
  showSourcesPreview = false;
  sources: EntityGenerationSource[] = [];
  sourcesLoading = false;
  sourcesError = false;
  private sourcesLoaded = false;

  constructor(
    private readonly questionsService: HomeEntityQuestionsService,
    private readonly generationService: HomeEntityGenerationService,
    private readonly planFeatures: PlanFeatureService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    public readonly lang: LanguageService,
  ) {}

  ngOnInit(): void {
    // Gate del boton de generacion: si el plan no tiene doc_generation, no se muestra.
    this.planFeatures
      .hasFeature(DOC_GENERATION_FEATURE_CODE)
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled) => {
        this.docGenerationEnabled = enabled;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get panelId(): string {
    return `home-entity-actions-${this.entity?.id ?? ''}`;
  }
  // H10: expandir y cargar preguntas
  toggleExpanded(): void {
    this.expanded = !this.expanded;
    if (this.expanded && !this.questionsLoaded && !this.questionsLoading) {
      this.loadQuestions();
    }
  }

  get questionsEmpty(): boolean {
    return this.questionsLoaded && !this.questionsError && this.questions.length === 0;
  }

  retryQuestions(): void {
    this.questionsLoaded = false;
    this.loadQuestions();
  }

  private loadQuestions(): void {
    if (!this.entity?.id) {
      return;
    }
    this.questionsLoading = true;
    this.questionsError = false;
    this.questionsService
      .getSuggestedQuestions(this.entity.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.questions = response.featureAvailable ? response.questions ?? [] : [];
          this.questionsLoading = false;
          this.questionsLoaded = true;
          this.cdr.markForCheck();
        },
        error: () => {
          this.questionsError = true;
          this.questionsLoading = false;
          this.questionsLoaded = true;
          this.cdr.markForCheck();
        },
      });
  }

  // H11: entrada al chat (texto precargado, envio manual)
  onQuestionSelected(question: EntitySuggestedQuestion): void {
    this.navigateToChat(question.text);
  }

  onAskAboutEntity(): void {
    const prefix = this.lang.translate('home.entityActions.askPrefill');
    const name = this.entity?.name ?? '';
    this.navigateToChat((prefix + ' ' + name).trim());
  }

  private navigateToChat(text: string): void {
    const state: HomeEntityChatNavigationState = {
      homeIaPrefill: { text, entityId: this.entity?.id ?? '' },
    };
    // NO se ejecuta la query: el chat solo precarga el input; el usuario confirma y envia.
    void this.router.navigate(['/chat'], { state });
  }

  // H12: generacion desde entidad con confirmacion explicita de fuentes
  onGenerateClicked(): void {
    this.showSourcesPreview = true;
    if (!this.sourcesLoaded && !this.sourcesLoading) {
      this.loadSources();
    }
  }

  get sourcesEmpty(): boolean {
    return this.sourcesLoaded && !this.sourcesError && this.sources.length === 0;
  }

  get canConfirmGeneration(): boolean {
    return this.sourcesLoaded && !this.sourcesError && this.sources.length > 0;
  }

  retrySources(): void {
    this.sourcesLoaded = false;
    this.loadSources();
  }

  cancelGeneration(): void {
    this.showSourcesPreview = false;
  }

  private loadSources(): void {
    if (!this.entity?.id) {
      return;
    }
    this.sourcesLoading = true;
    this.sourcesError = false;
    this.generationService
      .getGenerationSources(this.entity.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Si el backend degrada la feature (race con el gate del boton), se oculta el preview.
          if (!response.featureAvailable) {
            this.docGenerationEnabled = false;
            this.showSourcesPreview = false;
          }
          this.sources = response.sources ?? [];
          this.sourcesLoading = false;
          this.sourcesLoaded = true;
          this.cdr.markForCheck();
        },
        error: () => {
          this.sourcesError = true;
          this.sourcesLoading = false;
          this.sourcesLoaded = true;
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Confirmacion EXPLICITA: solo aqui se lanza el wizard de generacion R2 existente, pasando las
   * fuentes visibles como suggestedSources. El wizard revalida contra el catalogo real y exige un
   * segundo clic de "Generar": nunca hay generacion silenciosa ni reindexado.
   */
  confirmGeneration(): void {
    if (!this.canConfirmGeneration) {
      return;
    }
    const suggestedSources: SelectableSource[] = this.sources.map((source) => ({
      documentId: source.documentId,
      filename: source.filename,
    }));
    const state: GenerateNavigationState = { suggestedSources };
    void this.router.navigate(['/generate'], { state });
  }

  trackQuestion(_: number, question: EntitySuggestedQuestion): string {
    return question.text;
  }

  trackSource(_: number, source: EntityGenerationSource): string {
    return source.documentId;
  }
}
